import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { money, fmtDate, fmtTime12 } from "@/lib/format";
import { toast } from "sonner";
import { CheckCircle2, MapPin, Phone, MessageCircle, XCircle, CalendarClock, Scissors, Loader2 } from "lucide-react";

export default function BookingSuccess() {
  const { id } = useParams();
  const [appt, setAppt] = useState(null);
  const [cfg, setCfg] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => api.get(`/public/appointment/${id}`).then((r) => setAppt(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); api.get("/public/config").then((r) => setCfg(r.data)); }, [id]);

  const cancel = async () => {
    if (!window.confirm("¿Seguro que deseas cancelar tu cita?")) return;
    try { await api.post(`/public/appointment/${id}/cancel`); toast.success("Cita cancelada"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const waLink = () => {
    const phone = (cfg.whatsapp || "").replace(/\D/g, "");
    const text = `Hola! Tengo una cita en ${cfg.business_name} el ${fmtDate(appt.start)} a las ${fmtTime12(appt.start)}. Dirección: ${cfg.address}`;
    return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0f0f0f]"><Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" /></div>;
  if (!appt) return <div className="min-h-screen flex items-center justify-center bg-[#0f0f0f] text-[#a3a3a3]">Cita no encontrada.</div>;

  const cancelled = appt.status === "cancelled";

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-[#f5f5f5] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          {cancelled ? <XCircle className="w-16 h-16 text-[#ef4444] mx-auto mb-3" /> : <CheckCircle2 className="w-16 h-16 text-[#16a34a] mx-auto mb-3" />}
          <h1 className="text-2xl font-extrabold">{cancelled ? "Cita cancelada" : "¡Cita confirmada!"}</h1>
          <p className="text-[#a3a3a3] mt-1">{cancelled ? "Esta cita fue cancelada." : appt?.client_email ? "Te enviamos los detalles a tu correo." : "Guarda esta pantalla o toma captura con los detalles de tu cita."}</p>
        </div>

        <div className="nk-card p-6 space-y-3" data-testid="booking-detail">
          <Row label="Servicio" value={appt.service_name} />
          <Row label="Barbero" value={appt.barber_name} />
          <Row label="Fecha" value={fmtDate(appt.start)} />
          <Row label="Hora" value={fmtTime12(appt.start)} />
          <Row label="Cliente" value={appt.client_name} />
          {appt.discount_amount > 0 && <Row label="Descuento" value={`- ${money(appt.discount_amount)}`} />}
          <div className="border-t border-[#2a2a2a] pt-3 flex justify-between">
            <span className="font-bold">Total</span>
            <span className="font-bold text-[#d4af37] text-lg">{money(appt.price - (appt.discount_amount || 0))}</span>
          </div>
        </div>

        {!cancelled && (
          <div className="grid grid-cols-2 gap-3 mt-4">
            <Link to={`/reschedule/${id}`} className="nk-btn-ghost flex items-center justify-center gap-2" data-testid="btn-reschedule"><CalendarClock className="w-4 h-4" />Reagendar</Link>
            <button onClick={cancel} className="nk-btn-ghost flex items-center justify-center gap-2 text-[#ef4444] border-[#ef4444]/30" data-testid="btn-cancel"><XCircle className="w-4 h-4" />Cancelar</button>
            <a href={waLink()} target="_blank" rel="noreferrer" className="nk-btn col-span-2 flex items-center justify-center gap-2" data-testid="btn-whatsapp"><MessageCircle className="w-4 h-4" />Compartir por WhatsApp</a>
          </div>
        )}

        <div className="nk-card p-5 mt-4 text-sm text-[#a3a3a3] space-y-2">
          <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#d4af37]" />{cfg.address}</p>
          <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#d4af37]" />{cfg.phone}</p>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-[#737373] hover:text-[#d4af37] text-sm flex items-center justify-center gap-1"><Scissors className="w-4 h-4" />Volver al inicio</Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-sm"><span className="text-[#737373]">{label}</span><span className="text-[#f5f5f5] font-medium">{value}</span></div>;
}
